import type { EventSubscription } from 'expo-modules-core';

import NativeVeepooSDK from './NativeVeepooSDK.js';
import type {
  AutoMeasureSetting,
  BatteryInfo,
  BluetoothStatus,
  ConnectOptions,
  ConnectionStatus,
  DaySummaryData,
  DeviceFunctions,
  DeviceVersion,
  HalfHourData,
  OriginData,
  PasswordData,
  PersonalInfo,
  ScanOptions,
  SleepData,
  SocialMsgData,
  SportStepData,
  Spo2OriginData,
  VeepooEvent,
  VeepooError,
  VeepooEventPayload,
} from './types.js';
import type {
  CacheDomain,
  CacheEntryMeta,
  CacheReadOptions,
  CacheScope,
  CacheStatus,
  CacheValue,
  BloodGlucosePoint,
  BloodOxygenPoint,
  BloodPressurePoint,
  ConnectionSession,
  ConnectionSnapshot,
  DiagnosticOptions,
  DailyHealthSnapshot,
  DeviceProfile,
  HeartRatePoint,
  MeasurementResultMap,
  MeasurementState,
  MeasurementType,
  MetricItemQuery,
  MetricPointBase,
  MetricSourceType,
  ReadOptions,
  SDKState,
  SportPoint,
  StressPoint,
  SyncDomain,
  SyncHealthOptions,
  SyncState,
  SyncTaskResult,
  TemperaturePoint,
  UnifiedDiagnostic,
  UnifiedDiagnostics,
  UnifiedEventMap,
  UnifiedResult,
  VeepooUnifiedAPI,
} from './unified-api.js';
import {
  isPasswordAccepted,
  normalizeBatteryInfo,
  normalizeBloodGlucoseLevel,
  normalizeBloodGlucoseMeasurement,
  normalizeConnectionStatus,
  normalizeDaySummaryData,
  normalizeDeviceFunctions,
  normalizeDeviceVersion,
  normalizeHalfHourDataList,
  normalizeMeasurementState,
  normalizeOriginDataList,
  normalizeSleepData,
  normalizeSocialMsgData,
  normalizeSportData,
  normalizeSpo2OriginDataList,
  normalizeStressMeasurement,
} from './normalizers.js';

type UnifiedListener<K extends keyof UnifiedEventMap> = (payload: UnifiedEventMap[K]) => void;
type AnyUnifiedListener = (payload: UnifiedEventMap[keyof UnifiedEventMap]) => void;

const DEFAULT_READ_OPTIONS: Required<Pick<ReadOptions, 'strategy' | 'maxAgeMs' | 'saveToCache'>> = {
  strategy: 'device-first',
  maxAgeMs: 5 * 60 * 1000,
  saveToCache: true,
};

const CONNECT_READY_TIMEOUT_MS = 20_000;
const DEFAULT_SYNC_INCLUDE: SyncDomain[] = ['sleep', 'sport', 'origin5m', 'origin30m', 'daySummary', 'spo2Origin'];

interface CacheRecord<T> {
  value: T;
  meta: CacheEntryMeta;
}

interface PendingSyncBuffers {
  origin5m: OriginData[];
  origin30m: HalfHourData[];
  spo2Origin: Spo2OriginData[];
}

class VeepooUnifiedSDKImpl implements VeepooUnifiedAPI {
  private listeners = new Map<keyof UnifiedEventMap, Set<AnyUnifiedListener>>();
  private nativeSubscriptions: EventSubscription[] = [];
  private cache = new Map<string, CacheRecord<unknown>>();
  private rawListenersBound = false;
  private sdkState: SDKState = {
    initialized: false,
    scanning: false,
    connectedDeviceId: null,
    ready: false,
  };
  private authenticated = false;
  private lastBluetoothStatus: BluetoothStatus | null = null;
  private lastPasswordData: PasswordData | null = null;
  private measurementStates = new Map<MeasurementType, MeasurementState>();
  private syncState: SyncState = { status: 'idle' };
  private activeSyncTaskId: string | null = null;
  private pendingSyncBuffers: PendingSyncBuffers | null = null;
  private pendingSyncDate: string | null = null;

  async init(): Promise<void> {
    this.bindRawListeners();
    await NativeVeepooSDK.init();
    this.sdkState.initialized = true;
  }

  destroy(): void {
    this.nativeSubscriptions.forEach((subscription) => {
      subscription.remove();
    });
    this.nativeSubscriptions = [];
    this.listeners.clear();
    this.rawListenersBound = false;
    this.sdkState = {
      initialized: false,
      scanning: false,
      connectedDeviceId: null,
      ready: false,
    };
    this.authenticated = false;
    this.lastBluetoothStatus = null;
    this.lastPasswordData = null;
    this.measurementStates.clear();
    this.syncState = { status: 'idle' };
    this.activeSyncTaskId = null;
    this.pendingSyncDate = null;
    this.pendingSyncBuffers = null;
  }

  getSDKState(): SDKState {
    return { ...this.sdkState };
  }

  async getBluetoothStatus(): Promise<BluetoothStatus> {
    if (this.lastBluetoothStatus) {
      return this.lastBluetoothStatus;
    }

    const enabled = await NativeVeepooSDK.isBluetoothEnabled();
    return {
      state: enabled ? 'poweredOn' : 'poweredOff',
      stateName: enabled ? 'poweredOn' : 'poweredOff',
      authorization: 'allowedAlways',
      authorizationName: 'allowedAlways',
      isScanning: this.sdkState.scanning,
      pendingScanStart: false,
    };
  }

  async requestPermissions() {
    return NativeVeepooSDK.requestPermissions();
  }

  async startScan(options?: ScanOptions): Promise<void> {
    await NativeVeepooSDK.startScan(options);
    this.sdkState.scanning = true;
  }

  async stopScan(): Promise<void> {
    await NativeVeepooSDK.stopScan();
    this.sdkState.scanning = false;
  }

  async connect(deviceId: string, options?: ConnectOptions): Promise<UnifiedResult<ConnectionSession>> {
    this.bindRawListeners();
    const readyPromise = this.waitForReady(deviceId, CONNECT_READY_TIMEOUT_MS);
    await NativeVeepooSDK.connect(deviceId, options);
    const readyPayload = await readyPromise;
    const timestamp = Date.now();
    const data: ConnectionSession = {
      deviceId,
      status: 'ready',
      authenticated: this.authenticated,
      deviceVersion: this.lastPasswordData?.deviceVersion,
      deviceNumber: this.lastPasswordData?.deviceNumber,
      isOadModel: readyPayload.isOadModel,
    };

    return {
      data,
      source: 'device',
      deviceId,
      timestamp,
    };
  }

  async disconnect(deviceId?: string): Promise<void> {
    const targetDeviceId = deviceId ?? this.sdkState.connectedDeviceId;
    if (!targetDeviceId) {
      return;
    }

    await NativeVeepooSDK.disconnect(targetDeviceId);
    this.sdkState.ready = false;
    this.authenticated = false;
  }

  async getConnectionState(deviceId?: string): Promise<ConnectionSnapshot> {
    const targetId = deviceId ?? this.sdkState.connectedDeviceId ?? undefined;
    const status = targetId ? await NativeVeepooSDK.getConnectionStatus(targetId) : 'disconnected';

    return {
      deviceId: targetId,
      status,
      authenticated: this.authenticated,
      ready: this.sdkState.ready,
    };
  }

  async verifyPassword(password = '0000', is24Hour = false): Promise<UnifiedResult<PasswordData>> {
    const result = await NativeVeepooSDK.verifyPassword(password, is24Hour);
    const timestamp = Date.now();
    this.lastPasswordData = result;
    this.authenticated = isPasswordAccepted(result.status);

    return {
      data: result,
      source: 'device',
      deviceId: this.sdkState.connectedDeviceId ?? undefined,
      timestamp,
    };
  }

  async getDeviceProfile(options?: ReadOptions): Promise<UnifiedResult<DeviceProfile>> {
    const resolvedOptions = this.resolveReadOptions(options);
    const deviceId = this.requireConnectedDeviceId();
    const cacheKey = this.createCacheKey('device-profile', { deviceId });

    return this.readThrough(cacheKey, resolvedOptions, async () => {
      const [battery, functions, version, socialMsg] = await Promise.all([
        this.safeDataCall(() => NativeVeepooSDK.readBattery()),
        this.safeDataCall(() => NativeVeepooSDK.readDeviceFunctions()),
        this.safeDataCall(() => NativeVeepooSDK.readDeviceVersion()),
        this.safeDataCall(() => NativeVeepooSDK.readSocialMsgData()),
      ]);

      return {
        deviceId,
        battery: battery ? normalizeBatteryInfo(battery) : undefined,
        functions: functions ? normalizeDeviceFunctions(functions) : undefined,
        version: version ? normalizeDeviceVersion(version) : undefined,
        socialMsg: socialMsg ? normalizeSocialMsgData(socialMsg) : undefined,
        password: this.lastPasswordData ?? undefined,
      };
    }, {
      domain: 'device-profile',
      deviceId,
    });
  }

  async getBatteryInfo(options?: ReadOptions): Promise<UnifiedResult<BatteryInfo>> {
    const deviceId = this.requireConnectedDeviceId();
    return this.readDeviceValue('battery', { deviceId }, options, async () => normalizeBatteryInfo(await NativeVeepooSDK.readBattery()));
  }

  async getDeviceFunctions(options?: ReadOptions): Promise<UnifiedResult<DeviceFunctions>> {
    const deviceId = this.requireConnectedDeviceId();
    return this.readDeviceValue('device-functions', { deviceId }, options, async () => normalizeDeviceFunctions(await NativeVeepooSDK.readDeviceFunctions()));
  }

  async getDeviceVersion(options?: ReadOptions): Promise<UnifiedResult<DeviceVersion>> {
    const deviceId = this.requireConnectedDeviceId();
    return this.readDeviceValue('device-version', { deviceId }, options, async () => normalizeDeviceVersion(await NativeVeepooSDK.readDeviceVersion()));
  }

  async getSocialMessageSettings(options?: ReadOptions): Promise<UnifiedResult<SocialMsgData>> {
    const deviceId = this.requireConnectedDeviceId();
    return this.readDeviceValue('social-message-settings', { deviceId }, options, async () => normalizeSocialMsgData(await NativeVeepooSDK.readSocialMsgData()));
  }

  async syncHealthData(options?: SyncHealthOptions): Promise<SyncTaskResult> {
    const startedAt = Date.now();
    const taskId = `sync_${startedAt}`;
    const include = options?.include ?? DEFAULT_SYNC_INCLUDE;
    const deviceId = this.requireConnectedDeviceId();
    const date = this.todayString();

    this.activeSyncTaskId = taskId;
    this.pendingSyncDate = date;
    this.pendingSyncBuffers = {
      origin5m: [],
      origin30m: [],
      spo2Origin: [],
    };
    this.syncState = {
      taskId,
      status: 'running',
      progress: 0,
      currentDay: 1,
      totalDays: options?.days ?? 1,
      lastSyncAt: startedAt,
    };

    try {
      if (include.includes('deviceProfile')) {
        await this.getDeviceProfile({ strategy: 'device-first', saveToCache: options?.saveToCache ?? true });
      }

      if (include.includes('sleep')) {
        await this.getSleepData(date, { strategy: 'device-first', saveToCache: options?.saveToCache ?? true });
      }

      if (include.includes('sport')) {
        await this.getSportData(date, { strategy: 'device-first', saveToCache: options?.saveToCache ?? true });
      }

      if (include.includes('daySummary')) {
        await this.getDaySummaryData(date, { strategy: 'device-first', saveToCache: options?.saveToCache ?? true });
      }

      if (include.includes('origin5m') || include.includes('origin30m') || include.includes('spo2Origin')) {
        await NativeVeepooSDK.startReadOriginData();
        this.flushPendingOriginBuffers(deviceId, date);
      }

      this.syncState = {
        taskId,
        status: 'completed',
        progress: 1,
        currentDay: options?.days ?? 1,
        totalDays: options?.days ?? 1,
        lastSyncAt: Date.now(),
      };
      this.emit('sync.completed', { taskId, success: true, timestamp: Date.now() });
      return { taskId, startedAt };
    } catch (error) {
      const veepooError = this.toVeepooError(error);
      this.syncState = {
        taskId,
        status: 'failed',
        progress: this.syncState.progress,
        currentDay: this.syncState.currentDay,
        totalDays: this.syncState.totalDays,
        lastSyncAt: Date.now(),
        error: veepooError,
      };
      this.emit('error', veepooError);
      throw error;
    } finally {
      this.activeSyncTaskId = null;
      this.pendingSyncDate = null;
      this.pendingSyncBuffers = null;
    }
  }

  async cancelSyncHealthData(taskId?: string): Promise<void> {
    if (!taskId || taskId === this.activeSyncTaskId) {
      this.activeSyncTaskId = null;
      this.pendingSyncDate = null;
      this.pendingSyncBuffers = null;
      this.syncState = {
        ...this.syncState,
        status: 'idle',
      };
    }
  }

  getLastSyncState(): SyncState {
    return { ...this.syncState };
  }

  async getSleepData(date?: string, options?: ReadOptions): Promise<UnifiedResult<SleepData>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const cacheKey = this.createCacheKey('sleep', { deviceId, date: normalizedDate });

    return this.readThrough(cacheKey, this.resolveReadOptions(options), async () => {
      const result = await NativeVeepooSDK.readSleepData(normalizedDate);
      return normalizeSleepData(result, normalizedDate);
    }, {
      domain: 'sleep',
      deviceId,
      date: normalizedDate,
    });
  }

  async getSportData(date?: string, options?: ReadOptions): Promise<UnifiedResult<SportStepData>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const dayOffset = this.toDayOffset(normalizedDate);
    const cacheKey = this.createCacheKey('sport', { deviceId, date: normalizedDate });

    return this.readThrough(cacheKey, this.resolveReadOptions(options), async () => {
      const result = await NativeVeepooSDK.readSportStepData(dayOffset === 0 ? normalizedDate : normalizedDate);
      return normalizeSportData(result, normalizedDate);
    }, {
      domain: 'sport',
      deviceId,
      date: normalizedDate,
    });
  }

  async getOrigin5mData(date?: string, options?: ReadOptions): Promise<UnifiedResult<OriginData[]>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const cacheKey = this.createCacheKey('origin5m', { deviceId, date: normalizedDate });
    const dayOffset = this.toDayOffset(normalizedDate);

    return this.readThrough(cacheKey, this.resolveReadOptions(options), async () => normalizeOriginDataList(await NativeVeepooSDK.readOriginData(dayOffset)), {
      domain: 'origin5m',
      deviceId,
      date: normalizedDate,
    });
  }

  async getOrigin30mData(date?: string, options?: ReadOptions): Promise<UnifiedResult<HalfHourData[]>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const cacheKey = this.createCacheKey('origin30m', { deviceId, date: normalizedDate });
    const resolvedOptions = this.resolveReadOptions(options);
    const cached = this.getFreshCache<HalfHourData[]>(cacheKey, resolvedOptions.maxAgeMs);

    if (resolvedOptions.strategy === 'cache-only') {
      if (!cached) throw new Error(`No cached origin30m data for ${normalizedDate}`);
      return this.fromCacheRecord(cached);
    }

    if (resolvedOptions.strategy === 'cache-first' && cached) {
      return this.fromCacheRecord(cached);
    }

    await this.syncHealthData({ include: ['origin30m'], saveToCache: resolvedOptions.saveToCache });
    const refreshed = this.getFreshCache<HalfHourData[]>(cacheKey, resolvedOptions.maxAgeMs);
    if (!refreshed) {
      throw new Error(`Origin30m data unavailable for ${normalizedDate}`);
    }
    return this.fromCacheRecord(refreshed);
  }

  async getDaySummaryData(date?: string, options?: ReadOptions): Promise<UnifiedResult<DaySummaryData>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const cacheKey = this.createCacheKey('day-summary', { deviceId, date: normalizedDate });
    const dayOffset = this.toDayOffset(normalizedDate);

    return this.readThrough(cacheKey, this.resolveReadOptions(options), async () => normalizeDaySummaryData(await NativeVeepooSDK.readDaySummaryData(dayOffset), normalizedDate), {
      domain: 'day-summary',
      deviceId,
      date: normalizedDate,
    });
  }

  async getSpo2OriginData(date?: string, options?: ReadOptions): Promise<UnifiedResult<Spo2OriginData[]>> {
    const deviceId = this.requireConnectedDeviceId();
    const normalizedDate = this.normalizeDateInput(date);
    const cacheKey = this.createCacheKey('spo2-origin', { deviceId, date: normalizedDate });
    const resolvedOptions = this.resolveReadOptions(options);
    const cached = this.getFreshCache<Spo2OriginData[]>(cacheKey, resolvedOptions.maxAgeMs);

    if (resolvedOptions.strategy === 'cache-only') {
      if (!cached) throw new Error(`No cached spo2 origin data for ${normalizedDate}`);
      return this.fromCacheRecord(cached);
    }

    if (resolvedOptions.strategy === 'cache-first' && cached) {
      return this.fromCacheRecord(cached);
    }

    await this.syncHealthData({ include: ['spo2Origin'], saveToCache: resolvedOptions.saveToCache });
    const refreshed = this.getFreshCache<Spo2OriginData[]>(cacheKey, resolvedOptions.maxAgeMs);
    if (!refreshed) {
      throw new Error(`Spo2 origin data unavailable for ${normalizedDate}`);
    }
    return this.fromCacheRecord(refreshed);
  }

  async getDailyHealthSnapshot(date?: string, options?: ReadOptions): Promise<UnifiedResult<DailyHealthSnapshot>> {
    const normalizedDate = this.normalizeDateInput(date);
    const [sleep, sport, origin5m, origin30m, summary, spo2Origin] = await Promise.all([
      this.safeUnifiedCall(() => this.getSleepData(normalizedDate, options)),
      this.safeUnifiedCall(() => this.getSportData(normalizedDate, options)),
      this.safeUnifiedCall(() => this.getOrigin5mData(normalizedDate, options)),
      this.safeUnifiedCall(() => this.getOrigin30mData(normalizedDate, options)),
      this.safeUnifiedCall(() => this.getDaySummaryData(normalizedDate, options)),
      this.safeUnifiedCall(() => this.getSpo2OriginData(normalizedDate, options)),
    ]);

    const timestamp = Date.now();
    return {
      data: {
        date: normalizedDate,
        sleep: sleep?.data,
        sport: sport?.data,
        origin5m: origin5m?.data,
        origin30m: origin30m?.data,
        summary: summary?.data,
        spo2Origin: spo2Origin?.data,
      },
      source: 'memory-cache',
      deviceId: this.sdkState.connectedDeviceId ?? undefined,
      timestamp,
      cachedAt: timestamp,
    };
  }

  async getHeartRateList(date?: string, options?: ReadOptions): Promise<UnifiedResult<HeartRatePoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.heartValue > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              value: item.heartValue,
              stepCount: item.stepValue > 0 ? item.stepValue : undefined,
              distance: item.disValue > 0 ? item.disValue : undefined,
              calories: item.calValue > 0 ? item.calValue : undefined,
              sportValue: item.sportValue > 0 ? item.sportValue : undefined,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.heartValue ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              value: item.heartValue!,
              stepCount: item.stepValue,
              distance: item.disValue,
              calories: item.calValue,
              sportValue: item.sportValue,
            }))
        );
      },
      async () => {
        const result = await this.getDaySummaryData(normalizedDate, options);
        return this.mapDerivedList(result, (data) =>
          data.rateList
            .filter((item) => item.rate > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'daySummary'),
              value: item.rate,
            }))
        );
      },
    ]);
  }

  async getHeartRateItem(query?: MetricItemQuery, options?: ReadOptions): Promise<UnifiedResult<HeartRatePoint> | null> {
    return this.getMetricItemFromList(() => this.getHeartRateList(query?.date, options), query);
  }

  async getLatestHeartRate(date?: string, options?: ReadOptions): Promise<UnifiedResult<HeartRatePoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getHeartRateItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('heartRate', options, (result, timestamp) =>
          result.value && result.value > 0
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(timestamp), 'measurement', timestamp),
                value: result.value,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getBloodPressureList(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodPressurePoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.systolic > 0 && item.diastolic > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              systolic: item.systolic,
              diastolic: item.diastolic,
            }))
        );
      },
      async () => {
        const result = await this.getDaySummaryData(normalizedDate, options);
        return this.mapDerivedList(result, (data) =>
          data.bpList
            .filter((item) => item.high > 0 && item.low > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'daySummary'),
              systolic: item.high,
              diastolic: item.low,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.systolic ?? 0) > 0 && (item.diastolic ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              systolic: item.systolic!,
              diastolic: item.diastolic!,
            }))
        );
      },
    ]);
  }

  async getBloodPressureItem(
    query?: MetricItemQuery,
    options?: ReadOptions
  ): Promise<UnifiedResult<BloodPressurePoint> | null> {
    return this.getMetricItemFromList(() => this.getBloodPressureList(query?.date, options), query);
  }

  async getLatestBloodPressure(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodPressurePoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getBloodPressureItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('bloodPressure', options, (result, timestamp) =>
          result.systolic && result.diastolic
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(timestamp), 'measurement', timestamp),
                systolic: result.systolic,
                diastolic: result.diastolic,
                pulse: result.pulse,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getBloodOxygenList(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodOxygenPoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getSpo2OriginData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.value > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'spo2Origin'),
              spo2: item.value,
              heartRate: item.heartValue > 0 ? item.heartValue : undefined,
              isHypoxia: item.isHypoxia === 1,
              cardiacLoad: item.cardiacLoad > 0 ? item.cardiacLoad : undefined,
              sportValue: item.sportValue > 0 ? item.sportValue : undefined,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.spo2Value > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              spo2: item.spo2Value,
              heartRate: item.heartValue > 0 ? item.heartValue : undefined,
              sportValue: item.sportValue > 0 ? item.sportValue : undefined,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.spo2Value ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              spo2: item.spo2Value!,
            }))
        );
      },
    ]);
  }

  async getBloodOxygenItem(
    query?: MetricItemQuery,
    options?: ReadOptions
  ): Promise<UnifiedResult<BloodOxygenPoint> | null> {
    return this.getMetricItemFromList(() => this.getBloodOxygenList(query?.date, options), query);
  }

  async getLatestBloodOxygen(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodOxygenPoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getBloodOxygenItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('bloodOxygen', options, (result, timestamp) =>
          result.value && result.value > 0
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(timestamp), 'measurement', timestamp),
                spo2: result.value,
                heartRate: result.rate,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getTemperatureList(date?: string, options?: ReadOptions): Promise<UnifiedResult<TemperaturePoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.tempValue > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              temperature: item.tempValue,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.tempValue ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              temperature: item.tempValue!,
            }))
        );
      },
    ]);
  }

  async getTemperatureItem(query?: MetricItemQuery, options?: ReadOptions): Promise<UnifiedResult<TemperaturePoint> | null> {
    return this.getMetricItemFromList(() => this.getTemperatureList(query?.date, options), query);
  }

  async getLatestTemperature(date?: string, options?: ReadOptions): Promise<UnifiedResult<TemperaturePoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getTemperatureItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('temperature', options, (result, timestamp) =>
          result.value && result.value > 0
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(timestamp), 'measurement', timestamp),
                temperature: result.value,
                originalTemp: result.originalTemp,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getStressList(date?: string, options?: ReadOptions): Promise<UnifiedResult<StressPoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.stressValue > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              stress: item.stressValue,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.stressValue ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              stress: item.stressValue!,
            }))
        );
      },
    ]);
  }

  async getStressItem(query?: MetricItemQuery, options?: ReadOptions): Promise<UnifiedResult<StressPoint> | null> {
    return this.getMetricItemFromList(() => this.getStressList(query?.date, options), query);
  }

  async getLatestStress(date?: string, options?: ReadOptions): Promise<UnifiedResult<StressPoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getStressItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('stress', options, (result, timestamp) =>
          result.stress >= 0
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(result.timestamp ?? timestamp), 'measurement', result.timestamp ?? timestamp),
                stress: result.stress,
                progress: result.progress,
                status: result.status,
                state: result.state,
                reason: result.reason,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getBloodGlucoseList(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodGlucosePoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.glucose ?? item.bloodGlucose ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              glucose: item.glucose ?? item.bloodGlucose!,
              level: normalizeBloodGlucoseLevel(item.bloodGlucoseLevel as string | number | undefined),
              rawBloodGlucose: item.bloodGlucose,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.bloodGlucose ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              glucose: item.bloodGlucose!,
              rawBloodGlucose: item.bloodGlucose,
            }))
        );
      },
    ]);
  }

  async getBloodGlucoseItem(
    query?: MetricItemQuery,
    options?: ReadOptions
  ): Promise<UnifiedResult<BloodGlucosePoint> | null> {
    return this.getMetricItemFromList(() => this.getBloodGlucoseList(query?.date, options), query);
  }

  async getLatestBloodGlucose(date?: string, options?: ReadOptions): Promise<UnifiedResult<BloodGlucosePoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    const historical = await this.getBloodGlucoseItem({ date: normalizedDate }, options);
    const live = normalizedDate === this.todayString()
      ? await this.getMeasurementPoint('bloodGlucose', options, (result, timestamp) =>
          result.glucose && result.glucose > 0
            ? {
                ...this.createMetricPointBase(normalizedDate, this.timeFromTimestamp(result.timestamp ?? timestamp), 'measurement', result.timestamp ?? timestamp),
                glucose: result.glucose,
                level: result.level,
                progress: result.progress,
                status: result.status,
                state: result.state,
              }
            : null
        )
      : null;

    return this.pickLatestMetricResult(historical, live);
  }

  async getSportList(date?: string, options?: ReadOptions): Promise<UnifiedResult<SportPoint[]>> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.resolveDerivedMetricList(normalizedDate, [
      async () => {
        const result = await this.getDaySummaryData(normalizedDate, options);
        return this.mapDerivedList(result, (data) =>
          data.sportList
            .filter((item) => item.step > 0 || item.cal > 0 || item.dis > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'daySummary'),
              stepCount: item.step,
              distance: item.dis > 0 ? item.dis : undefined,
              calories: item.cal > 0 ? item.cal : undefined,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin5mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => item.stepValue > 0 || item.disValue > 0 || item.calValue > 0 || item.sportValue > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin5m'),
              stepCount: item.stepValue,
              distance: item.disValue > 0 ? item.disValue : undefined,
              calories: item.calValue > 0 ? item.calValue : undefined,
              sportValue: item.sportValue > 0 ? item.sportValue : undefined,
              met: item.met > 0 ? item.met : undefined,
            }))
        );
      },
      async () => {
        const result = await this.getOrigin30mData(normalizedDate, options);
        return this.mapDerivedList(result, (items) =>
          items
            .filter((item) => (item.stepValue ?? 0) > 0 || (item.disValue ?? 0) > 0 || (item.calValue ?? 0) > 0 || (item.sportValue ?? 0) > 0)
            .map((item) => ({
              ...this.createMetricPointBase(normalizedDate, item.time, 'origin30m'),
              stepCount: item.stepValue ?? 0,
              distance: item.disValue,
              calories: item.calValue,
              sportValue: item.sportValue,
              met: item.met,
            }))
        );
      },
    ]);
  }

  async getSportItem(query?: MetricItemQuery, options?: ReadOptions): Promise<UnifiedResult<SportPoint> | null> {
    return this.getMetricItemFromList(() => this.getSportList(query?.date, options), query);
  }

  async getLatestSport(date?: string, options?: ReadOptions): Promise<UnifiedResult<SportPoint> | null> {
    const normalizedDate = this.normalizeDateInput(date);
    return this.getSportItem({ date: normalizedDate }, options);
  }

  async startMeasurement(type: MeasurementType): Promise<void> {
    const action = this.measurementAction(type, 'start');
    await action();
  }

  async stopMeasurement(type: MeasurementType): Promise<void> {
    const action = this.measurementAction(type, 'stop');
    await action();
  }

  getMeasurementState<K extends MeasurementType>(type: K): MeasurementState<MeasurementResultMap[K]> | null {
    const state = this.measurementStates.get(type);
    return (state as MeasurementState<MeasurementResultMap[K]> | undefined) ?? null;
  }

  async getLastMeasurementResult<K extends MeasurementType>(
    type: K,
    options?: ReadOptions
  ): Promise<UnifiedResult<MeasurementResultMap[K]> | null> {
    const deviceId = this.sdkState.connectedDeviceId ?? undefined;
    const cacheKey = this.createCacheKey('measurement', { deviceId, date: type });
    const resolvedOptions = this.resolveReadOptions(options);
    const cached = this.getFreshCache<MeasurementResultMap[K]>(cacheKey, resolvedOptions.maxAgeMs);

    if (!cached) {
      return null;
    }

    return this.fromCacheRecord(cached);
  }

  async syncPersonalInfo(info: PersonalInfo): Promise<boolean> {
    return NativeVeepooSDK.syncPersonalInfo(info);
  }

  async getAutoMeasureSettings(options?: ReadOptions): Promise<UnifiedResult<AutoMeasureSetting[]>> {
    const deviceId = this.requireConnectedDeviceId();
    return this.readDeviceValue('auto-measure-settings', { deviceId }, options, () => NativeVeepooSDK.readAutoMeasureSetting());
  }

  async updateAutoMeasureSetting(setting: Partial<AutoMeasureSetting>): Promise<UnifiedResult<AutoMeasureSetting[]>> {
    const deviceId = this.requireConnectedDeviceId();
    const result = await NativeVeepooSDK.modifyAutoMeasureSetting(setting);
    return this.storeAndWrap(result, {
      key: this.createCacheKey('auto-measure-settings', { deviceId }),
      domain: 'auto-measure-settings',
      deviceId,
    });
  }

  async setLanguage(language: string): Promise<boolean> {
    return NativeVeepooSDK.setLanguage(language as never);
  }

  getCacheStatus(deviceId?: string): CacheStatus {
    const keys = [...this.cache.values()]
      .filter(({ meta }) => !deviceId || meta.deviceId === deviceId)
      .map(({ meta }) => meta.key);
    const lastUpdatedAt = [...this.cache.values()]
      .filter(({ meta }) => !deviceId || meta.deviceId === deviceId)
      .reduce<number | undefined>((latest, entry) => {
        if (!latest || entry.meta.cachedAt > latest) {
          return entry.meta.cachedAt;
        }
        return latest;
      }, undefined);

    return {
      keys,
      count: keys.length,
      lastUpdatedAt,
    };
  }

  getCachedValue<T = unknown>({ key }: CacheReadOptions): CacheValue<T> | null {
    const entry = this.cache.get(key) as CacheRecord<T> | undefined;
    if (!entry) {
      return null;
    }

    return this.fromCacheRecord(entry);
  }

  async clearCache(scope?: CacheScope): Promise<void> {
    for (const [key, entry] of this.cache.entries()) {
      if (scope?.deviceId && entry.meta.deviceId !== scope.deviceId) {
        continue;
      }
      if (scope?.domains && !scope.domains.includes(entry.meta.domain)) {
        continue;
      }
      if (scope?.date && entry.meta.date !== scope.date) {
        continue;
      }
      this.cache.delete(key);
    }
  }

  invalidateCache(key: string | string[]): void {
    for (const target of Array.isArray(key) ? key : [key]) {
      this.cache.delete(target);
    }
  }

  async warmupCache(options: SyncHealthOptions): Promise<SyncTaskResult> {
    return this.syncHealthData(options);
  }

  async getDiagnostics(options?: DiagnosticOptions): Promise<UnifiedDiagnostics> {
    const sdkState = this.getSDKState();
    const deviceId = options?.deviceId ?? sdkState.connectedDeviceId ?? undefined;
    const date = options?.date ?? this.todayString();
    const originPreviewLimit = options?.originPreviewLimit ?? 5;
    const cacheStatus = this.getCacheStatus(deviceId);
    const origin30mKey = this.createCacheKey('origin30m', { deviceId, date });
    const origin30mEntry = this.cache.get(origin30mKey) as CacheRecord<HalfHourData[]> | undefined;
    const origin30mPreview = (origin30mEntry?.value ?? []).slice(-originPreviewLimit).reverse();
    const stressMeasurement = (this.getMeasurementState('stress')?.result as MeasurementResultMap['stress'] | undefined) ?? null;
    const bloodGlucoseMeasurement =
      (this.getMeasurementState('bloodGlucose')?.result as MeasurementResultMap['bloodGlucose'] | undefined) ?? null;

    return {
      sdkState,
      cacheStatus,
      origin30mPreview,
      stressMeasurement,
      bloodGlucoseMeasurement,
      diagnostics: {
        origin30m: this.buildOrigin30mDiagnostics(origin30mPreview),
        stress: this.buildStressDiagnostics(stressMeasurement),
        bloodGlucose: this.buildBloodGlucoseDiagnostics(bloodGlucoseMeasurement),
      },
    };
  }

  on<K extends keyof UnifiedEventMap>(event: K, listener: UnifiedListener<K>): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as AnyUnifiedListener);
    return this;
  }

  off<K extends keyof UnifiedEventMap>(event: K, listener: UnifiedListener<K>): this {
    this.listeners.get(event)?.delete(listener as AnyUnifiedListener);
    return this;
  }

  once<K extends keyof UnifiedEventMap>(event: K, listener: UnifiedListener<K>): this {
    const wrapped = ((payload: UnifiedEventMap[K]) => {
      this.off(event, wrapped);
      listener(payload);
    }) as UnifiedListener<K>;
    return this.on(event, wrapped);
  }

  removeAllListeners(event?: keyof UnifiedEventMap): this {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
    return this;
  }

  private bindRawListeners(): void {
    if (this.rawListenersBound) {
      return;
    }
    this.rawListenersBound = true;

    this.addNativeListener('deviceFound', ({ device, timestamp }) => {
      this.emit('scan.deviceFound', { device, timestamp });
    });

    this.addNativeListener('deviceConnected', ({ deviceId }) => {
      this.sdkState.connectedDeviceId = deviceId;
      this.sdkState.ready = false;
      this.emitConnectionChanged(deviceId, 'connected');
    });

    this.addNativeListener('deviceDisconnected', ({ deviceId }) => {
      this.sdkState.connectedDeviceId = null;
      this.sdkState.ready = false;
      this.authenticated = false;
      this.emitConnectionChanged(deviceId, 'disconnected');
    });

    this.addNativeListener('deviceConnectStatus', ({ deviceId, status }) => {
      this.emitConnectionChanged(deviceId, normalizeConnectionStatus(status));
    });

    this.addNativeListener('deviceReady', ({ deviceId, isOadModel }) => {
      this.sdkState.connectedDeviceId = deviceId;
      this.sdkState.ready = true;
      this.emitConnectionChanged(deviceId, 'ready');
      this.emit('device.ready', { deviceId, isOadModel });
    });

    this.addNativeListener('bluetoothStateChanged', (payload) => {
      this.lastBluetoothStatus = payload;
    });

    this.addNativeListener('passwordData', ({ deviceId, data }) => {
      this.lastPasswordData = data;
      this.authenticated = isPasswordAccepted(data.status);
      if (this.authenticated) {
        this.emitConnectionChanged(deviceId, this.sdkState.ready ? 'ready' : 'connected');
      }
    });

    this.addNativeListener('batteryData', ({ deviceId, data }) => {
      this.storeAndEmitCache(normalizeBatteryInfo(data), {
        key: this.createCacheKey('battery', { deviceId }),
        domain: 'battery',
        deviceId,
      });
    });

    this.addNativeListener('deviceFunction', ({ deviceId, functions, data }) => {
      const value = functions ?? data;
      if (value) {
        this.storeAndEmitCache(normalizeDeviceFunctions(value), {
          key: this.createCacheKey('device-functions', { deviceId }),
          domain: 'device-functions',
          deviceId,
        });
      }
    });

    this.addNativeListener('deviceVersion', ({ deviceId, version }) => {
      this.storeAndEmitCache(normalizeDeviceVersion(version), {
        key: this.createCacheKey('device-version', { deviceId }),
        domain: 'device-version',
        deviceId,
      });
    });

    this.addNativeListener('socialMsgData', ({ deviceId, data }) => {
      this.storeAndEmitCache(normalizeSocialMsgData(data), {
        key: this.createCacheKey('social-message-settings', { deviceId }),
        domain: 'social-message-settings',
        deviceId,
      });
    });

    this.addNativeListener('readOriginProgress', ({ progress }) => {
      if (!this.activeSyncTaskId) {
        return;
      }
      this.syncState = {
        ...this.syncState,
        status: 'running',
        progress: progress.progress,
        currentDay: progress.currentDay,
        totalDays: progress.totalDays,
      };
      this.emit('sync.progress', {
        taskId: this.activeSyncTaskId,
        progress,
      });
    });

    this.addNativeListener('readOriginComplete', ({ success }) => {
      if (!this.activeSyncTaskId) {
        return;
      }
      this.flushPendingOriginBuffers(this.requireConnectedDeviceId(), this.pendingSyncDate ?? this.todayString());
      this.syncState = {
        ...this.syncState,
        status: success ? 'completed' : 'failed',
        progress: success ? 1 : this.syncState.progress,
        lastSyncAt: Date.now(),
      };
      this.emit('sync.completed', {
        taskId: this.activeSyncTaskId,
        success,
        timestamp: Date.now(),
      });
    });

    this.addNativeListener('originFiveMinuteData', ({ data }) => {
      this.pendingSyncBuffers?.origin5m.push(...normalizeOriginDataList([data]));
    });

    this.addNativeListener('originHalfHourData', ({ data }) => {
      this.pendingSyncBuffers?.origin30m.push(...normalizeHalfHourDataList([data]));
    });

    this.addNativeListener('originSpo2Data', ({ data }) => {
      this.pendingSyncBuffers?.spo2Origin.push(...normalizeSpo2OriginDataList(data));
    });

    this.addNativeListener('sleepData', (payload) => {
      const deviceId = payload.deviceId;
      const data = normalizeSleepData(payload.data, payload.date);
      this.storeAndEmitCache(data, {
        key: this.createCacheKey('sleep', { deviceId, date: payload.date }),
        domain: 'sleep',
        deviceId,
        date: payload.date,
      });
    });

    this.addNativeListener('sportStepData', ({ deviceId, date, data }) => {
      this.storeAndEmitCache(normalizeSportData(data, date), {
        key: this.createCacheKey('sport', { deviceId, date }),
        domain: 'sport',
        deviceId,
        date,
      });
    });

    this.addNativeListener('heartRateTestResult', ({ result }) => {
      this.updateMeasurementState('heartRate', result.state, result.progress, result);
    });

    this.addNativeListener('bloodPressureTestResult', ({ result }) => {
      this.updateMeasurementState('bloodPressure', result.state, result.progress, result);
    });

    this.addNativeListener('bloodOxygenTestResult', ({ result }) => {
      this.updateMeasurementState('bloodOxygen', result.state, result.progress, result);
    });

    this.addNativeListener('temperatureTestResult', ({ result }) => {
      this.updateMeasurementState('temperature', result.state, result.progress, result);
    });

    this.addNativeListener('stressData', ({ data }) => {
      const normalized = normalizeStressMeasurement(data);
      this.updateMeasurementState('stress', normalized.state, normalized.progress, normalized);
    });

    this.addNativeListener('bloodGlucoseData', ({ data }) => {
      const normalized = normalizeBloodGlucoseMeasurement(data);
      this.updateMeasurementState('bloodGlucose', normalized.state, normalized.progress, normalized);
    });

    this.addNativeListener('error', (error) => {
      this.emit('error', error);
    });
  }

  private emit<K extends keyof UnifiedEventMap>(event: K, payload: UnifiedEventMap[K]): void {
    const handlers = this.listeners.get(event);
    if (!handlers) {
      return;
    }

    for (const handler of handlers) {
      try {
        handler(payload);
      } catch (error) {
        console.error(`Error in unified event listener for ${String(event)}:`, error);
      }
    }
  }

  private emitConnectionChanged(deviceId: string, status: ConnectionStatus): void {
    this.emit('connection.changed', {
      deviceId,
      status,
      authenticated: this.authenticated,
      ready: this.sdkState.ready,
    });
  }

  private addNativeListener<K extends VeepooEvent>(
    event: K,
    listener: (payload: VeepooEventPayload[K]) => void
  ): void {
    const subscription = NativeVeepooSDK.addListener(event, (payload) => {
      listener(payload as VeepooEventPayload[K]);
    });
    this.nativeSubscriptions.push(subscription);
  }

  private waitForReady(deviceId: string, timeoutMs: number): Promise<VeepooEventPayload['deviceReady']> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.off('device.ready', onReady);
        this.off('error', onError);
        reject(new Error(`Timed out waiting for device ${deviceId} to become ready`));
      }, timeoutMs);

      const cleanup = () => {
        clearTimeout(timer);
        this.off('device.ready', onReady);
        this.off('error', onError);
      };

      const onReady = (payload: UnifiedEventMap['device.ready']) => {
        if (payload.deviceId !== deviceId) {
          return;
        }
        cleanup();
        resolve(payload);
      };

      const onError = (payload: VeepooEventPayload['error']) => {
        cleanup();
        reject(new Error(payload.message));
      };

      this.on('device.ready', onReady);
      this.on('error', onError);
    });
  }

  private requireConnectedDeviceId(): string {
    const deviceId = this.sdkState.connectedDeviceId;
    if (!deviceId) {
      throw new Error('No connected device');
    }
    return deviceId;
  }

  private resolveReadOptions(options?: ReadOptions): Required<Pick<ReadOptions, 'strategy' | 'maxAgeMs' | 'saveToCache'>> {
    return {
      strategy: options?.strategy ?? DEFAULT_READ_OPTIONS.strategy,
      maxAgeMs: options?.maxAgeMs ?? DEFAULT_READ_OPTIONS.maxAgeMs,
      saveToCache: options?.saveToCache ?? DEFAULT_READ_OPTIONS.saveToCache,
    };
  }

  private createCacheKey(domain: CacheDomain, meta: { deviceId?: string; date?: string }): string {
    return [domain, meta.deviceId ?? 'global', meta.date ?? 'latest'].join(':');
  }

  private getFreshCache<T>(key: string, maxAgeMs: number): CacheRecord<T> | null {
    const entry = this.cache.get(key) as CacheRecord<T> | undefined;
    if (!entry) {
      return null;
    }

    const stale = Date.now() - entry.meta.cachedAt > maxAgeMs;
    if (stale) {
      return {
        ...entry,
        meta: {
          ...entry.meta,
          stale: true,
        },
      };
    }

    return entry;
  }

  private fromCacheRecord<T>(entry: CacheRecord<T>): CacheValue<T> {
    const stale = entry.meta.stale ?? false;
    return {
      data: entry.value,
      source: 'memory-cache',
      deviceId: entry.meta.deviceId,
      timestamp: entry.meta.timestamp,
      cachedAt: entry.meta.cachedAt,
      stale,
      meta: entry.meta,
    };
  }

  private storeAndWrap<T>(value: T, meta: { key: string; domain: CacheDomain; deviceId?: string; date?: string }): UnifiedResult<T> {
    const timestamp = Date.now();
    const record: CacheRecord<T> = {
      value,
      meta: {
        key: meta.key,
        domain: meta.domain,
        deviceId: meta.deviceId,
        date: meta.date,
        timestamp,
        cachedAt: timestamp,
      },
    };
    this.cache.set(meta.key, record);
    this.emit('cache.updated', {
      key: meta.key,
      domain: meta.domain,
      deviceId: meta.deviceId,
      date: meta.date,
      timestamp,
    });

    return {
      data: value,
      source: 'device',
      deviceId: meta.deviceId,
      timestamp,
      cachedAt: timestamp,
    };
  }

  private storeAndEmitCache<T>(value: T, meta: { key: string; domain: CacheDomain; deviceId?: string; date?: string }): void {
    this.storeAndWrap(value, meta);
  }

  private async readDeviceValue<T>(
    domain: CacheDomain,
    meta: { deviceId?: string; date?: string },
    options: ReadOptions | undefined,
    loader: () => Promise<T>
  ): Promise<UnifiedResult<T>> {
    const cacheKey = this.createCacheKey(domain, meta);
    return this.readThrough(cacheKey, this.resolveReadOptions(options), loader, {
      domain,
      deviceId: meta.deviceId,
      date: meta.date,
    });
  }

  private async readThrough<T>(
    cacheKey: string,
    options: Required<Pick<ReadOptions, 'strategy' | 'maxAgeMs' | 'saveToCache'>>,
    loader: () => Promise<T>,
    meta: { domain: CacheDomain; deviceId?: string; date?: string }
  ): Promise<UnifiedResult<T>> {
    const cached = this.getFreshCache<T>(cacheKey, options.maxAgeMs);

    if (options.strategy === 'cache-only') {
      if (!cached) {
        throw new Error(`No cache available for key: ${cacheKey}`);
      }
      return this.fromCacheRecord(cached);
    }

    if (options.strategy === 'cache-first' && cached && !cached.meta.stale) {
      return this.fromCacheRecord(cached);
    }

    if (options.strategy === 'device-only' || options.strategy === 'device-first' || options.strategy === 'cache-first') {
      try {
        const value = await loader();
        if (options.saveToCache) {
          return this.storeAndWrap(value, { key: cacheKey, ...meta });
        }
        return {
          data: value,
          source: 'device',
          deviceId: meta.deviceId,
          timestamp: Date.now(),
        };
      } catch (error) {
        if ((options.strategy === 'device-first' || options.strategy === 'cache-first') && cached) {
          return this.fromCacheRecord(cached);
        }
        throw error;
      }
    }

    if (cached) {
      return this.fromCacheRecord(cached);
    }

    throw new Error(`Unable to resolve data for key: ${cacheKey}`);
  }

  private todayString(): string {
    return this.normalizeDateInput();
  }

  private normalizeDateInput(date?: string): string {
    if (date) {
      return date;
    }
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDayOffset(date: string): number {
    const today = new Date(this.todayString());
    const target = new Date(date);
    const diffMs = today.getTime() - target.getTime();
    return Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)));
  }

  private measurementAction(type: MeasurementType, phase: 'start' | 'stop'): () => Promise<void> {
    const actions = {
      heartRate: {
        start: () => NativeVeepooSDK.startHeartRateTest(),
        stop: () => NativeVeepooSDK.stopHeartRateTest(),
      },
      bloodPressure: {
        start: () => NativeVeepooSDK.startBloodPressureTest(),
        stop: () => NativeVeepooSDK.stopBloodPressureTest(),
      },
      bloodOxygen: {
        start: () => NativeVeepooSDK.startBloodOxygenTest(),
        stop: () => NativeVeepooSDK.stopBloodOxygenTest(),
      },
      temperature: {
        start: () => NativeVeepooSDK.startTemperatureTest(),
        stop: () => NativeVeepooSDK.stopTemperatureTest(),
      },
      stress: {
        start: () => NativeVeepooSDK.startStressTest(),
        stop: () => NativeVeepooSDK.stopStressTest(),
      },
      bloodGlucose: {
        start: () => NativeVeepooSDK.startBloodGlucoseTest(),
        stop: () => NativeVeepooSDK.stopBloodGlucoseTest(),
      },
    } satisfies Record<MeasurementType, Record<'start' | 'stop', () => Promise<void>>>;

    return actions[type][phase];
  }

  private async resolveDerivedMetricList<T extends MetricPointBase>(
    _date: string,
    loaders: Array<() => Promise<UnifiedResult<T[]>>>
  ): Promise<UnifiedResult<T[]>> {
    let emptyResult: UnifiedResult<T[]> | null = null;
    let lastError: unknown = null;

    for (const loader of loaders) {
      try {
        const result = await loader();
        if (result.data.length > 0) {
          return result;
        }
        if (!emptyResult) {
          emptyResult = result;
        }
      } catch (error) {
        lastError = error;
      }
    }

    if (emptyResult) {
      return emptyResult;
    }

    throw lastError ?? new Error('Metric data unavailable');
  }

  private mapDerivedList<TSource, TPoint>(
    base: UnifiedResult<TSource>,
    mapper: (data: TSource) => TPoint[]
  ): UnifiedResult<TPoint[]> {
    return this.withDerivedResult(base, mapper(base.data));
  }

  private withDerivedResult<T>(base: UnifiedResult<unknown>, data: T): UnifiedResult<T> {
    return {
      data,
      source: base.source,
      deviceId: base.deviceId,
      timestamp: base.timestamp,
      cachedAt: base.cachedAt,
      stale: base.stale,
    };
  }

  private async getMetricItemFromList<T extends MetricPointBase>(
    loader: () => Promise<UnifiedResult<T[]>>,
    query?: MetricItemQuery
  ): Promise<UnifiedResult<T> | null> {
    const listResult = await loader();
    const item = this.findMetricPoint(listResult.data, query);
    if (!item) {
      return null;
    }
    return this.withDerivedResult(listResult, item);
  }

  private findMetricPoint<T extends MetricPointBase>(items: T[], query?: MetricItemQuery): T | null {
    if (items.length === 0) {
      return null;
    }

    if (query?.timestamp != null) {
      return items.find((item) => item.timestamp === query.timestamp) ?? null;
    }

    if (query?.time) {
      const expected = this.normalizeTimeToken(query.time);
      return items.find((item) => this.normalizeTimeToken(item.time) === expected) ?? null;
    }

    return items.at(-1) ?? null;
  }

  private async getMeasurementPoint<K extends MeasurementType, T extends MetricPointBase>(
    type: K,
    options: ReadOptions | undefined,
    mapper: (result: MeasurementResultMap[K], timestamp: number) => T | null
  ): Promise<UnifiedResult<T> | null> {
    const measurement = await this.safeOptionalUnifiedCall(() => this.getLastMeasurementResult(type, options));
    if (!measurement) {
      return null;
    }

    const point = mapper(measurement.data, measurement.timestamp);
    if (!point) {
      return null;
    }

    return this.withDerivedResult(measurement, point);
  }

  private pickLatestMetricResult<T extends MetricPointBase>(
    historical: UnifiedResult<T> | null,
    live: UnifiedResult<T> | null
  ): UnifiedResult<T> | null {
    if (!historical) {
      return live;
    }
    if (!live) {
      return historical;
    }
    return live.data.timestamp >= historical.data.timestamp ? live : historical;
  }

  private createMetricPointBase(
    date: string,
    time: string,
    sourceType: MetricSourceType,
    timestamp = this.buildMetricTimestamp(date, time)
  ): MetricPointBase {
    return {
      date,
      time,
      timestamp,
      sourceType,
    };
  }

  private buildMetricTimestamp(date: string, time: string): number {
    const normalizedTime = this.normalizeTimeToken(time) || '00:00';
    const hasSeconds = normalizedTime.length > 5;
    const value = new Date(`${date}T${hasSeconds ? normalizedTime : `${normalizedTime}:00`}`).getTime();
    if (Number.isFinite(value)) {
      return value;
    }
    return new Date(`${date}T00:00:00`).getTime();
  }

  private normalizeTimeToken(time: string): string {
    const normalized = time.trim();
    if (!normalized) {
      return '';
    }
    if (/^\d{2}:\d{2}$/.test(normalized) || /^\d{2}:\d{2}:\d{2}$/.test(normalized)) {
      return normalized;
    }
    if (/^\d{1}:\d{2}$/.test(normalized)) {
      return `0${normalized}`;
    }
    return normalized;
  }

  private timeFromTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const seconds = `${date.getSeconds()}`.padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private updateMeasurementState<K extends MeasurementType>(
    type: K,
    state: string,
    progress: number | undefined,
    result: MeasurementResultMap[K]
  ): void {
    const normalizedState = normalizeMeasurementState(state);
    const nextState: MeasurementState<MeasurementResultMap[K]> = {
      type,
      state: normalizedState,
      progress,
      result,
      timestamp: Date.now(),
    };
    this.measurementStates.set(type, nextState as MeasurementState);
    this.storeAndEmitCache(result, {
      key: this.createCacheKey('measurement', {
        deviceId: this.sdkState.connectedDeviceId ?? undefined,
        date: type,
      }),
      domain: 'measurement',
      deviceId: this.sdkState.connectedDeviceId ?? undefined,
      date: type,
    });
    this.emit('measurement.updated', {
      type,
      state: nextState as MeasurementState,
    });
  }

  private flushPendingOriginBuffers(deviceId: string, date: string): void {
    if (!this.pendingSyncBuffers) {
      return;
    }

    if (this.pendingSyncBuffers.origin5m.length > 0) {
      this.storeAndEmitCache(this.pendingSyncBuffers.origin5m, {
        key: this.createCacheKey('origin5m', { deviceId, date }),
        domain: 'origin5m',
        deviceId,
        date,
      });
    }

    if (this.pendingSyncBuffers.origin30m.length > 0) {
      this.storeAndEmitCache(this.pendingSyncBuffers.origin30m, {
        key: this.createCacheKey('origin30m', { deviceId, date }),
        domain: 'origin30m',
        deviceId,
        date,
      });
    }

    if (this.pendingSyncBuffers.spo2Origin.length > 0) {
      this.storeAndEmitCache(this.pendingSyncBuffers.spo2Origin, {
        key: this.createCacheKey('spo2-origin', { deviceId, date }),
        domain: 'spo2-origin',
        deviceId,
        date,
      });
    }
  }

  private buildOrigin30mDiagnostics(items: HalfHourData[]): UnifiedDiagnostic[] {
    if (items.length === 0) {
      return [
        {
          level: 'info',
          title: 'Origin30m 暂无缓存',
          detail: '还没有 30 分钟统一数据，先执行一次历史同步后再查看诊断。',
        },
      ];
    }

    const missingSpo2 = items.filter((item) => item.spo2Value == null).length;
    const missingStress = items.filter((item) => item.stressValue == null).length;
    const missingTemp = items.filter((item) => item.tempValue == null).length;
    const missingGlucose = items.filter((item) => item.glucose == null && item.bloodGlucose == null).length;
    const diagnostics: UnifiedDiagnostic[] = [];

    if (missingSpo2 === items.length) {
      diagnostics.push({
        level: 'warning',
        title: 'Origin30m 缺少血氧字段',
        detail: '最近的统一 30 分钟数据都没有 spo2Value，说明平台补值仍可能不完整。',
      });
    }

    if (missingStress === items.length) {
      diagnostics.push({
        level: 'warning',
        title: 'Origin30m 缺少压力字段',
        detail: '最近的统一 30 分钟数据都没有 stressValue，建议继续核对 30 分钟压力聚合逻辑。',
      });
    }

    if (missingTemp === items.length) {
      diagnostics.push({
        level: 'info',
        title: 'Origin30m 体温字段为空',
        detail: 'tempValue 当前没有采到，可能设备不支持，也可能平台侧暂未补到统一层。',
      });
    }

    if (missingGlucose === items.length) {
      diagnostics.push({
        level: 'info',
        title: 'Origin30m 血糖字段为空',
        detail: '最近的统一 30 分钟数据都没有 glucose / bloodGlucose，Android 侧可能仍未补齐该映射。',
      });
    }

    if (diagnostics.length === 0) {
      diagnostics.push({
        level: 'info',
        title: 'Origin30m 结构看起来正常',
        detail: '最近的统一 30 分钟数据已带有部分健康字段，可继续关注更大样本下是否稳定。',
      });
    }

    return diagnostics;
  }

  private buildStressDiagnostics(value: MeasurementResultMap['stress'] | null): UnifiedDiagnostic[] {
    if (!value) {
      return [
        {
          level: 'info',
          title: 'Stress 暂无测量结果',
          detail: '先执行一次压力测试，再查看统一层诊断。',
        },
      ];
    }

    const diagnostics: UnifiedDiagnostic[] = [];

    if (value.timestamp == null || value.timestamp <= 0) {
      diagnostics.push({
        level: 'warning',
        title: 'Stress 缺少 timestamp',
        detail: '统一层应该始终补 timestamp，当前值异常。',
      });
    }

    if (value.state === 'error' && !value.reason) {
      diagnostics.push({
        level: 'warning',
        title: 'Stress 错误原因缺失',
        detail: '当前 state=error，但没有 reason，后续排障信息会不足。',
      });
    }

    if (value.state === 'testing' && value.isEnd) {
      diagnostics.push({
        level: 'warning',
        title: 'Stress 状态矛盾',
        detail: 'isEnd=true 但 state 仍是 testing，说明状态归一化仍有边界问题。',
      });
    }

    if (diagnostics.length === 0) {
      diagnostics.push({
        level: 'info',
        title: 'Stress 归一化状态正常',
        detail: `当前 state=${value.state}${value.reason ? `，reason=${value.reason}` : ''}。`,
      });
    }

    return diagnostics;
  }

  private buildBloodGlucoseDiagnostics(value: MeasurementResultMap['bloodGlucose'] | null): UnifiedDiagnostic[] {
    if (!value) {
      return [
        {
          level: 'info',
          title: 'BloodGlucose 暂无测量结果',
          detail: '先执行一次血糖测试，再查看统一层诊断。',
        },
      ];
    }

    const diagnostics: UnifiedDiagnostic[] = [];
    const normalizedLevels = new Set(['low', 'normal', 'high', 'unknown']);

    if (value.timestamp == null || value.timestamp <= 0) {
      diagnostics.push({
        level: 'warning',
        title: 'BloodGlucose 缺少 timestamp',
        detail: '统一层应该始终补 timestamp，当前值异常。',
      });
    }

    if (typeof value.level === 'string' && !normalizedLevels.has(value.level)) {
      diagnostics.push({
        level: 'warning',
        title: 'BloodGlucose level 仍未完全统一',
        detail: `当前 level=${value.level}，说明平台返回值还没完全收敛到 low/normal/high/unknown。`,
      });
    }

    if (value.state === 'error' && !value.reason && !value.error) {
      diagnostics.push({
        level: 'warning',
        title: 'BloodGlucose 错误信息不足',
        detail: '当前 state=error，但没有 reason 或 error 字段。',
      });
    }

    if (value.state === 'over' && (value.glucose == null || value.glucose <= 0)) {
      diagnostics.push({
        level: 'warning',
        title: 'BloodGlucose 完成值异常',
        detail: 'state=over 但 glucose 为空或 <= 0，需要继续检查平台换算逻辑。',
      });
    }

    if (diagnostics.length === 0) {
      diagnostics.push({
        level: 'info',
        title: 'BloodGlucose 归一化状态正常',
        detail: `当前 state=${value.state}${value.reason ? `，reason=${value.reason}` : ''}${value.level ? `，level=${value.level}` : ''}。`,
      });
    }

    return diagnostics;
  }

  private toVeepooError(error: unknown): VeepooError {
    if (typeof error === 'object' && error && 'code' in error && 'message' in error) {
      return error as VeepooError;
    }

    return {
      code: 'UNKNOWN',
      message: error instanceof Error ? error.message : String(error),
      deviceId: this.sdkState.connectedDeviceId ?? undefined,
    };
  }

  private async safeDataCall<T>(loader: () => Promise<T>): Promise<T | null> {
    try {
      return await loader();
    } catch {
      return null;
    }
  }

  private async safeUnifiedCall<T>(loader: () => Promise<UnifiedResult<T>>): Promise<UnifiedResult<T> | null> {
    try {
      return await loader();
    } catch {
      return null;
    }
  }

  private async safeOptionalUnifiedCall<T>(
    loader: () => Promise<UnifiedResult<T> | null>
  ): Promise<UnifiedResult<T> | null> {
    try {
      return await loader();
    } catch {
      return null;
    }
  }
}

const VeepooUnifiedSDK = new VeepooUnifiedSDKImpl();

export { VeepooUnifiedSDKImpl };
export default VeepooUnifiedSDK;
